/**
 * The 'xavior' theme — the reference + default StudioTheme (token-contract §5.3,
 * "Xavior(default)" in the THEME-picker mockup).
 *
 * This is the COMPLETE theme: every required ArtTokenMap key, every §2.6 layout
 * token, and tokens for all 6 required treatments + the 4 optional treatments.
 * It is the canonical look the foundation test exercises (the TRACTION slide
 * fixture + the doc section fixture) and the model the other 4 theme authors
 * build against. The other themes (boardroom/keynote/editorial/mono) are a later
 * stream and ship as minimal stubs until then.
 */
export const xavior = {
    id: 'xavior',
    label: 'Default',
    layoutLanguage: 'systematic',
    fontsUrl: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,600;1,6..72,400&display=swap',
    tokens: {
        // ── 2.1 typography ──
        '--xv-art-font-display': "'Inter', system-ui, -apple-system, sans-serif",
        '--xv-art-font-body': "'Inter', system-ui, -apple-system, sans-serif",
        '--xv-art-font-mono': "'SFMono-Regular', ui-monospace, Menlo, monospace",
        '--xv-art-font-weight-display': '700',
        '--xv-art-font-weight-body': '400',
        '--xv-art-tracking-display': '-0.02em',
        '--xv-art-tracking-eyebrow': '0.18em',
        // ── 2.2 surface ──
        '--xv-art-bg': '#ffffff',
        '--xv-art-bg-surface': '#f6f8fb',
        '--xv-art-bg-inverse': '#0b1220',
        '--xv-art-surface-hover': '#eef2f7',
        // ── 2.3 ink ──
        '--xv-art-ink': '#0b1220',
        '--xv-art-ink-secondary': '#3d4759',
        '--xv-art-ink-muted': '#7a869a',
        '--xv-art-ink-inverse': '#f6f8fb',
        // ── 2.4 accent ──
        '--xv-art-accent': '#0ea5e9',
        '--xv-art-accent-ink': '#ffffff',
        '--xv-art-accent-dim': 'rgba(14, 165, 233, 0.12)',
        '--xv-art-accent-2': '#6366f1',
        // ── 2.5 borders ──
        '--xv-art-border': '#e2e8f0',
        '--xv-art-border-strong': '#cbd5e1',
        // ── 2.6 layout ──
        '--xv-art-measure': '780px',
        '--xv-art-space-unit': '8px',
        '--xv-art-gutter': '64px',
        '--xv-art-radius': '12px',
        '--xv-art-radius-sm': '6px',
        '--xv-art-line-height': '1.6',
        '--xv-art-line-height-display': '1.1',
        // ── 2.7 stat primitives ──
        // Stat numerals track the resolved ink (#5339) — a brand kit's ink override
        // reaches the hero figure; renders byte-identical near-black without a kit.
        '--xv-art-stat-ink': 'var(--xv-art-ink, #0b1220)',
        '--xv-art-stat-positive': '#16a34a',
        '--xv-art-stat-negative': '#dc2626',
        '--xv-art-stat-size': 'clamp(3rem, 8vw, 6rem)',
        // ── 3.1 highlight ──
        // Track the resolved accent (default = the sky blue below) so an applied
        // brand kit's accent override reaches inline emphasis (#5319); renders
        // byte-identical blue without a kit.
        '--xv-art-highlight-ink': 'var(--xv-art-accent, #0ea5e9)',
        '--xv-art-highlight-bg': 'transparent',
        '--xv-art-highlight-weight': '600',
        '--xv-art-highlight-pad': '0',
        // ── 3.2 callout ──
        '--xv-art-callout-bg': '#f6f8fb',
        '--xv-art-callout-border': '#e2e8f0',
        // Callout body ink tracks the resolved ink (#5339).
        '--xv-art-callout-ink': 'var(--xv-art-ink, #0b1220)',
        '--xv-art-callout-radius': '12px',
        // Track the resolved accent so a brand kit's accent flows to the insight bar.
        '--xv-art-callout-accent-insight': 'var(--xv-art-accent, #0ea5e9)',
        '--xv-art-callout-accent-warning': '#f59e0b',
        '--xv-art-callout-accent-success': '#16a34a',
        // ── 3.3 stat ──
        // Stat labels track the resolved muted ink (#5352) — a brand kit's Muted
        // override reaches the "NET RETENTION" band labels.
        '--xv-art-stat-label-ink': 'var(--xv-art-ink-muted, #7a869a)',
        '--xv-art-stat-label-tracking': '0.06em',
        '--xv-art-stat-numeral-font': "'Inter', system-ui, sans-serif",
        // ── 3.4 quote ──
        // Quote ink tracks the resolved secondary ink (#5339).
        '--xv-art-quote-ink': 'var(--xv-art-ink-secondary, #3d4759)',
        // Accent-tracking left rule (sky-blue default; a kit's accent flows through).
        '--xv-art-quote-bar': 'var(--xv-art-accent, #0ea5e9)',
        '--xv-art-quote-font': "'Newsreader', Georgia, serif",
        '--xv-art-quote-size': '1.3rem',
        '--xv-art-quote-style': 'italic',
        // ── 3.5 eyebrow ──
        // The eyebrow kicker tracks the resolved muted ink (#5352).
        '--xv-art-eyebrow-ink': 'var(--xv-art-ink-muted, #7a869a)',
        '--xv-art-eyebrow-tracking': '0.18em',
        '--xv-art-eyebrow-size': '0.75rem',
        '--xv-art-eyebrow-transform': 'uppercase',
        // The eyebrow index/rule tracks the resolved accent — the "04 TRACTION"
        // index must wear a kit's accent, never a stale default blue (#5319).
        '--xv-art-eyebrow-rule': 'var(--xv-art-accent, #0ea5e9)',
        // ── 3.6 wordmark ──
        // The footer wordmark tracks the resolved muted ink (#5352).
        '--xv-art-wordmark-ink': 'var(--xv-art-ink-muted, #7a869a)',
        '--xv-art-wordmark-font': "'Inter', system-ui, sans-serif",
        '--xv-art-wordmark-size': '0.9rem',
        '--xv-art-wordmark-weight': '600',
        // ── 3.7 optional treatments ──
        // Links track the resolved accent (sky-blue default; a kit's accent flows through).
        '--xv-art-link-ink': 'var(--xv-art-accent, #0ea5e9)',
        '--xv-art-code-bg': '#f6f8fb',
        // Code ink tracks the resolved ink (#5339).
        '--xv-art-code-ink': 'var(--xv-art-ink, #0b1220)',
        // ── 4.2 logo slot (theme leaves logo empty → text wordmark renders) ──
        '--xv-art-logo-url': 'none',
        '--xv-art-logo-aspect': 'auto',
        '--xv-art-logo-height': '0.9rem',
        '--xv-art-logo-invert': '0',
    },
};
//# sourceMappingURL=xavior.js.map